class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    
    // Listen for cart updates to re-initialize upsells
    document.addEventListener('cart:updated', () => {
      if (this.classList.contains('active')) {
        this.handleCartUpdate();
      }
    });
    
    // Listen for DOM mutations in the drawer to catch any content updates
    this.setupMutationObserver();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
    
    // Initialize upsell functionality
    setTimeout(() => {
      this.initializeUpsells();
    }, 100);
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  // Cleanup method
  disconnectedCallback() {
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
      // Re-initialize upsells after content update
      this.initializeUpsells();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  initializeUpsells() {
    // Handle upsell add buttons
    this.querySelectorAll('.upsell-add-btn').forEach(button => {
      button.addEventListener('click', this.handleUpsellAdd.bind(this));
    });

    // Handle variant selectors for upsells
    this.querySelectorAll('.variant-selector').forEach(select => {
      select.addEventListener('change', this.handleVariantChange.bind(this));
    });

    // Initialize variant selection for each upsell item
    this.querySelectorAll('.upsell-item').forEach(item => {
      const selectors = item.querySelectorAll('.variant-selector');
      if (selectors.length > 0) {
        // Trigger change event on the first selector to set initial variant
        const event = new Event('change', { bubbles: true });
        selectors[0].dispatchEvent(event);
      }
    });
  }

  handleUpsellAdd(event) {
    const button = event.target;
    const productId = button.dataset.productId;
    const variantId = button.dataset.variantId;
    
    button.textContent = 'ADDING...';
    button.disabled = true;

    const formData = {
      'items': [{
        'id': variantId,
        'quantity': 1
      }]
    };

    fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(formData)
    })
    .then(response => response.json())
    .then(data => {
      // Refresh cart drawer content
      this.refreshCartDrawer();
    })
    .catch(error => {
      console.error('Error adding upsell to cart:', error);
      button.textContent = 'ADD';
      button.disabled = false;
    });
  }

  handleVariantChange(event) {
    const select = event.target;
    const upsellItem = select.closest('.upsell-item');
    const productId = upsellItem.dataset.productId;
    const addButton = upsellItem.querySelector('.upsell-add-btn');
    
    // Get product variants data - fixed selector
    const variantsScript = upsellItem.querySelector(`script.product-variants-data[data-product-id="${productId}"]`);
    if (!variantsScript) {
      console.error('Product variants data not found for product:', productId);
      return;
    }
    
    let productData;
    try {
      productData = JSON.parse(variantsScript.textContent);
    } catch (e) {
      console.error('Failed to parse product data:', e);
      return;
    }
    
    // Get all selected options from visible selectors only
    const selectedOptions = [];
    const selectors = upsellItem.querySelectorAll('.variant-selector');
    
    // For upsell products that are child products with preselected color,
    // we only need to match based on the visible selectors (size)
    selectors.forEach(selector => {
      selectedOptions.push(selector.value);
    });
    
    // Find matching variant based on visible options only
    const matchingVariant = this.findMatchingVariantForUpsell(productData.variants, selectedOptions);
    
    if (matchingVariant) {
      addButton.dataset.variantId = matchingVariant.id;
      
      // Update price if needed
      const priceElement = upsellItem.querySelector('.upsell-price');
      if (priceElement && matchingVariant.price) {
        // Format price using Shopify's money format
        const formattedPrice = this.formatMoney(matchingVariant.price);
        priceElement.textContent = formattedPrice;
      }
      
      // Update button state based on availability
      addButton.disabled = !matchingVariant.available;
      addButton.textContent = matchingVariant.available ? 'ADD' : 'SOLD OUT';
    } else {
      console.warn('No matching variant found for selected size:', selectedOptions);
      addButton.disabled = true;
      addButton.textContent = 'UNAVAILABLE';
    }
  }

  findMatchingVariant(variants, selectedOptions) {
    return variants.find(variant => {
      // Check if all options match
      return variant.options.every((option, index) => {
        return option === selectedOptions[index];
      });
    });
  }

  findMatchingVariantForUpsell(variants, selectedOptions) {
    // For upsell products (child products with preselected color),
    // we match based on the visible options only (typically just size)
    return variants.find(variant => {
      // Since we only have size selector visible, we need to match
      // the variant that has the selected size, regardless of other options
      if (selectedOptions.length === 1) {
        // Only size selector is visible, find variant with matching size
        // Assuming size is typically the last option in variant.options
        const variantSize = variant.options[variant.options.length - 1];
        return variantSize === selectedOptions[0];
      } else {
        // Multiple selectors visible, match in order
        return selectedOptions.every((option, index) => {
          // Match against the last N options where N is the number of visible selectors
          const variantOptionIndex = variant.options.length - selectedOptions.length + index;
          return variant.options[variantOptionIndex] === option;
        });
      }
    });
  }

  formatMoney(cents) {
    // Basic money formatting - you may want to use Shopify's money format
    const dollars = cents / 100;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(dollars);
  }

  refreshCartDrawer() {
    fetch(window.Shopify.routes.root + 'cart.js')
      .then(response => response.json())
      .then(cart => {
        // Trigger cart update
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
        
        // Refresh the drawer content
        fetch(`${window.Shopify.routes.root}?section_id=cart-drawer`)
          .then(response => response.text())
          .then(html => {
            const parser = new DOMParser();
            const newContent = parser.parseFromString(html, 'text/html');
            const newDrawerInner = newContent.querySelector('.drawer__inner');
            
            if (newDrawerInner) {
              this.querySelector('.drawer__inner').innerHTML = newDrawerInner.innerHTML;
              this.initializeUpsells(); // Re-initialize event listeners
            }
          });
      });
  }

  // Method to handle any cart content updates
  handleCartUpdate() {
    // Re-initialize upsells whenever cart content is updated
    setTimeout(() => {
      this.initializeUpsells();
    }, 100);
  }

  // Setup mutation observer to watch for content changes
  setupMutationObserver() {
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }
    
    this.mutationObserver = new MutationObserver((mutations) => {
      let shouldReinitialize = false;
      
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList' && mutation.target.closest('.drawer__inner')) {
          // Check if upsell elements were added or removed
          const addedNodes = Array.from(mutation.addedNodes);
          const removedNodes = Array.from(mutation.removedNodes);
          
          if (addedNodes.some(node => node.nodeType === 1 && (node.classList?.contains('upsell-item') || node.querySelector?.('.upsell-item'))) ||
              removedNodes.some(node => node.nodeType === 1 && (node.classList?.contains('upsell-item') || node.querySelector?.('.upsell-item')))) {
            shouldReinitialize = true;
          }
        }
      });
      
      if (shouldReinitialize) {
        this.handleCartUpdate();
      }
    });
    
    // Start observing
    this.mutationObserver.observe(this, {
      childList: true,
      subtree: true
    });
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }

  // Override the updateCartDrawer method to ensure upsells are re-initialized
  updateCartDrawer() {
    if (super.updateCartDrawer) {
      super.updateCartDrawer();
    }
    
    // Get the cart drawer element and re-initialize upsells
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && cartDrawer.handleCartUpdate) {
      cartDrawer.handleCartUpdate();
    }
  }

  // Override renderContents to ensure upsells are re-initialized after item removal
  renderContents(parsedState) {
    super.renderContents(parsedState);
    
    // Re-initialize upsells after content update
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && cartDrawer.handleCartUpdate) {
      cartDrawer.handleCartUpdate();
    }
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
